/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-unused-vars */

import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
  forwardRef,
  createContext,
  useContext,
  Dispatch,
  PropsWithChildren,
} from 'react';

import { InputChangeEventHandler, InputContextType, InputProps, InputSize } from './input.types';
import { EyeIcon, EyeInvisibleIcon, MagnifyingGlassIcon, CalendarIcon, XmarkIcon, Button } from '../../index';
import { addStyles, setInputValue } from '../../utils/helpers';
import { FormHelperText } from '../form-helper-text/form-helper-text';

const iconButtonStyle = 'absolute top-0 right-0 h-full w-10.5 flex justify-center items-center';
const leftIconClasses = 'h-5 fill-gray-600';

export const InputContext = createContext<InputContextType | undefined>(undefined);

export const useInputContext = () => {
  const context = useContext(InputContext);
  if (context === undefined) {
    throw new Error('InputContext must be within InputContext.Provider');
  }

  return context;
};

const hasPrefix = (type?: string): boolean => {
  return type === 'search' || type === 'date';
};

const hasSuffix = (type?: string): boolean => {
  return type === 'search' || type === 'date';
};

export const triggerOnChange = (element: HTMLElement, value: string) => {
  const input = element.closest('.input-wrapper')?.querySelector('input');
  if (input) {
    setInputValue(input, value);
  }
};

const getOnChangeHandler = (setValue: Dispatch<React.SetStateAction<string>>, onChange?: InputChangeEventHandler) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    if (onChange) {
      onChange(e);
    }
  };
};

const getInputClasses = (
  type?: React.HTMLInputTypeAttribute,
  error?: string | boolean,
  className?: string,
  containerSize?: InputSize
) => {
  return addStyles([
    'rounded-lg w-full focus:border-1 focus:border-midnight-900 focus:ring-midnight-900 my-px',
    containerSize === 'medium' ? 'h-8' : 'h-10.5',
    error ? 'border-red-600' : 'border-gray-600',
    hasPrefix(type) ? 'pl-9' : '',
    hasSuffix(type) ? 'pr-7.5 truncate' : '',
    className,
  ]);
};

const getShowXButton = (
  value: InputContextType['value'],
  type?: React.HTMLInputTypeAttribute,
  allowClear: InputContextType['allowClear'] = true
) => {
  return allowClear && (type === 'search' || type === 'date') && value.toString().length > 0;
};

const getInputType = (type?: React.HTMLInputTypeAttribute, hidden?: boolean) => {
  if (type === 'date') return 'text';
  if (type !== 'password') return type || 'text';
  return hidden ? type : 'text';
};

const Label = (props: InputProps) => {
  const { label, name } = props;
  return (
    <>
      {label && (
        <label htmlFor={name} className="pb-2 font-medium text-gray-900">
          {label}
        </label>
      )}
    </>
  );
};

const InputLeftIcon = () => {
  const { type } = useInputContext();

  let icon;
  if (type === 'search') icon = <MagnifyingGlassIcon className={leftIconClasses} />;
  else if (type === 'date') icon = <CalendarIcon className={leftIconClasses} />;

  return (
    <>
      {icon !== undefined && (
        <div className="absolute left-0 top-0 flex h-full w-10.5 items-center justify-center">
          <span>{icon}</span>
        </div>
      )}
    </>
  );
};

const XButton = () => {
  const { type, value, setValue, allowClear, containerSize } = useInputContext();
  const showXButton = getShowXButton(value, type, allowClear);

  const handleXClick = (event: React.MouseEvent<HTMLElement>) => {
    setValue('');
    triggerOnChange(event.currentTarget, '');
  };

  return (
    <>
      {showXButton && (
        <div className={iconButtonStyle}>
          <Button
            icon={XmarkIcon}
            title="Clear selection"
            onClick={(e) => {
              e.stopPropagation();
              handleXClick(e);
            }}
            type="text"
            size={containerSize === 'large' ? 'medium' : 'small'}
          />
        </div>
      )}
    </>
  );
};

const PasswordEyeWrapper = (props: PropsWithChildren<unknown>) => {
  const { type, hidden, setHidden } = useInputContext();

  const handleToggleVisible = useCallback(
    (event: { preventDefault: () => void }) => {
      event.preventDefault();
      setHidden(!hidden);
    },
    [setHidden, hidden]
  );

  return (
    <>
      {type === 'password' && (
        <div className={iconButtonStyle}>
          <span
            data-testid="password-eye"
            onClick={handleToggleVisible}
            className="cursor-pointer select-none"
            tabIndex={0}
          >
            {props.children}
          </span>
        </div>
      )}
    </>
  );
};

const PasswordEye = () => {
  const { hidden } = useInputContext();

  return (
    <PasswordEyeWrapper>
      {hidden ? <EyeInvisibleIcon className="h-6" /> : <EyeIcon className="h-6" />}
    </PasswordEyeWrapper>
  );
};

const HelperText = () => {
  const { helperProps, containerSize } = useInputContext();
  const size = helperProps?.size ?? containerSize ?? 'large';
  return <>{helperProps && <FormHelperText {...{ ...helperProps, type: 'helper', size: size }} />}</>;
};

const ErrorText = () => {
  const { error, errorProps, containerSize } = useInputContext();
  const props = errorProps || { display: error };
  const size = errorProps?.size ?? containerSize ?? 'large';
  return <>{error || errorProps ? <FormHelperText {...{ ...props, type: 'error', size: size }} /> : undefined}</>;
};

const InputWrapper = (props: PropsWithChildren<unknown>) => {
  return (
    <div className="flex flex-col">
      <div className="input-wrapper browser-style-none relative">
        <InputLeftIcon />
        {props.children}
        <PasswordEye />
        <XButton />
      </div>
      <HelperText />
      <ErrorText />
    </div>
  );
};

const InputContent = forwardRef<HTMLInputElement>(function InputContent(props, ref) {
  const numberScrollRef = useRef<HTMLInputElement | null>(null);

  const {
    onValueChange,
    setValue,
    setHidden,
    allowClear,
    onFocus,
    onKeyUp,
    onWheel,
    className,
    hidden,
    value,
    name,
    label,
    type,
    error,
    errorProps,
    containerSize,
    numberScroll,
    ...rest
  } = useInputContext();

  const inputType = useMemo(() => getInputType(type, hidden), [type, hidden]);
  const inputClasses = useMemo(
    () => getInputClasses(type, errorProps?.visible ?? error, className, containerSize),
    [type, containerSize, error, errorProps, className]
  );

  useEffect(() => {
    const element = numberScrollRef.current;

    if (!element) return;

    const disableNumberScrollCallback = (event: Event) => {
      const typeIsNumber = type === 'number';
      const inputIsActiveEl = document.activeElement === element;
      const scrollIsDisabled = numberScroll === false;
      if (typeIsNumber && inputIsActiveEl && scrollIsDisabled) event.preventDefault();
    };

    element.addEventListener('wheel', disableNumberScrollCallback, { passive: false });
    return () => element.removeEventListener('wheel', disableNumberScrollCallback);
  }, [type, numberScroll, numberScrollRef]);

  const setRefs = (element: HTMLInputElement | null) => {
    if (typeof ref === 'function') {
      ref(element);
    } else if (ref) {
      ref.current = element;
    }
    numberScrollRef.current = element;
  };

  return (
    <InputWrapper>
      <input
        data-testid="input-field"
        aria-label={label}
        {...rest}
        ref={setRefs}
        onChange={onValueChange}
        onFocus={onFocus}
        onKeyUp={onKeyUp}
        name={name}
        type={inputType}
        value={value}
        className={inputClasses}
      />
    </InputWrapper>
  );
});

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const {
    onChange,
    type,
    value: initialValue = '',
    allowClear = true,
    containerSize = 'large',
    numberScroll = false,
    ...rest
  } = props;

  // State
  const [hidden, setHidden] = useState(type === 'password');
  const [value, setValue] = useState<string>(initialValue.toString());

  const onValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => getOnChangeHandler(setValue, onChange)(e),
    [setValue, onChange]
  );

  useEffect(() => {
    setValue(initialValue.toString());
  }, [initialValue]);

  const inputContextValue = useMemo(
    () => ({
      ...rest,
      type,
      setHidden,
      onChange,
      hidden,
      value,
      setValue,
      onValueChange,
      allowClear,
      containerSize,
      numberScroll,
    }),
    [rest, type, setHidden, onChange, hidden, value, setValue, onValueChange, allowClear, containerSize, numberScroll]
  );

  return (
    <InputContext.Provider value={inputContextValue}>
      <div>
        <Label {...props} />
        <InputContent ref={ref} />
      </div>
    </InputContext.Provider>
  );
});

export default Input;
